import {
  calculateSum,
  CHART_COLORS, CHART_FONTS,
  CHART_STYLES,
  HighchartsOptions,
} from 'src/app/domain/shared/component/pie-chart-simple/highcharts-common/highcharts-global-const';
import {
  TotalPpChartDTO,
  ValueCreationChartsDTO,
  ValueCreationOptions,
} from "../../../../../../fund-performance/dto/value-creation-charts-classes";
import * as Highcharts from "highcharts";


export class ValueCreationOptionsService {

  static getOptions(chartsData: ValueCreationChartsDTO, multiplicator = 1): ValueCreationOptions {
    const totalPPOptions = chartsData.totalPP ? this.getTotalPPChartOptions(chartsData.totalPP, multiplicator) : null;
    const netFpDTOOptions = chartsData.netFpDTO ? this.getNetFpDTOChartOptions(chartsData.netFpDTO) : null;

    return {
      totalPP: totalPPOptions,
      netFpDTO: netFpDTOOptions,
    };
  }

  private static getTotalPPChartOptions(totalPP: TotalPpChartDTO, multiplicator = 1): Partial<HighchartsOptions> {
    return {
      chart: {
        events: CHART_STYLES.eventForScrollingWithManualRedraw,
      },
      customRange: 19,
      xAxis: {
        labels: {
          style: { fontSize: CHART_FONTS.font4, color: '#78909C' },
        },
        categories: totalPP.period,
        type: 'category',
        crosshair: true,
        scrollbar: CHART_STYLES.scrollbar,
      },
      yAxis: [{
        title: {
          text: undefined,
        },
        labels: {
          // eslint-disable-next-line no-template-curly-in-string
          format: '{value}M',
          style: { fontSize: CHART_FONTS.font4, color: CHART_COLORS.black12 },
        },
        stackLabels: {
          enabled: true,
          style: { color: CHART_COLORS.black12, fontSize: CHART_FONTS.font4 },
          formatter(): string {
            const sum = calculateSum(this.axis.series, this.x);
            if (sum !== 0 && ((sum < 0 && this.isNegative) || (sum >= 0 && !this.isNegative))) {
              const decimals = sum > 100 ? 0 : 1;
              return Highcharts.numberFormat(sum, decimals);
            }
          },
        },
      },
      {
        title: {
          text: 'MOIC',
          style: { color: CHART_COLORS.black12, fontSize: CHART_FONTS.font3 },
        },
        labels: {
          format: '{value}',
          style: { color: CHART_COLORS.black12, fontSize: CHART_FONTS.font4 },
        },
        opposite: true,
      }],
      plotOptions: {
        column: {
          stacking: 'normal',
          width: 100,
          dataLabels: CHART_STYLES.dataLabelsWithDecimals,
        },
      },
      legend: {
        itemStyle: { fontSize: '0.75rem' },
        align: 'right',
        verticalAlign: 'top',
        floating: false,
      },
      responsive: CHART_STYLES.responsiveLegendTwoYAxis,
      tooltip: {
        shared: true,
        borderColor: '#EEEFF0',
        backgroundColor: 'white',
        borderRadius: 12,
        shadow: false,
        useHTML: true,
        headerFormat: '<span style="font-size: 0.8rem"><b><span style="color: #00948E">{point.key.isPredicted}</span>\
        {point.key.period}</b></span><br>',
        style: { fontSize: CHART_FONTS.font4 },
        valueSuffix: ' M',
        pointFormat: '{series.name}:<b> $ {point.y:.2f}M</b><br>',
        pointFormatter(): string {
          const colorDot = '<span style="color: ' + this.color + '">&#9679;</span>';
          // MOIC: x1.5
          // Investment: 15M
          if (this.y) {
            return colorDot + ' ' + this.series.name + (this.series.name === 'MOIC' ? '<b>: x' : ': <b>')
              + this.y.toFixed(2) + (this.series.name === 'MOIC' ? '' : 'M') + '</b><div>';
          }
          return colorDot + ' ' + this.series.name + ': <b>-</b><br>';
        },
      },
      series: [{
        name: 'Net gain/(loss)',
        type: 'column',
        yAxis: 0,
        data: totalPP.gain.map((gain) => gain * multiplicator),
        color: '#5CC3BB',
      }, {
        name: 'Investment',
        type: 'column',
        yAxis: 0,
        data: totalPP.inv.map((inv) => inv * multiplicator),
        color: '#5E6F78',
      },
      {
        name: 'MOIC',
        type: 'spline',
        yAxis: 1,
        data: totalPP.moic,
        color: '#5E6F78',
        marker: {
          enabled: false,
          lineWidth: 3,
          fillColor: 'white',
        },
      }],
    };
  }

  private static getNetFpDTOChartOptions(netFpDTO): Partial<HighchartsOptions> {
    return {
      chart: {
        style: {
          fontFamily: 'Inter',
        },
        events: CHART_STYLES.eventForScrollingWithManualRedraw,
      },
      xAxis: {
        labels: {
          style: { fontSize: CHART_FONTS.font4, color: '#78909C' },
        },
        categories: netFpDTO.period,
        type: 'category',
        crosshair: true,
        scrollbar: CHART_STYLES.scrollbar,
      },
      yAxis: [{ // Primary yAxis
        title: {
          text: undefined,
        },
        labels: {
          // eslint-disable-next-line no-template-curly-in-string
          format: '{value}M',
          style: { fontSize: CHART_FONTS.font4, color: CHART_COLORS.black12 },
        },
        stackLabels: {
          enabled: true,
          allowOverlap: false,
          y: -5,
          style: { color: CHART_COLORS.black12, fontSize: CHART_FONTS.font4 },
          formatter: function (): string {
            if (this.total !== 0) {
              const decimals = this.total > 100 ? 0 : 1;
              return Highcharts.numberFormat(this.total, decimals);
            }
          },
        },
      },
      { // Secondary yAxis
        title: {
          text: 'TVPI',
          style: { color: CHART_COLORS.black12, fontSize: CHART_FONTS.font3 },
        },
        labels: {
          format: '{value}',
          style: { color: CHART_COLORS.black12, fontSize: CHART_FONTS.font4 },
        },
        opposite: true,
      }],
      tooltip: {
        shared: true,
        borderColor: '#EEEFF0',
        backgroundColor: 'white',
        borderRadius: 12,
        shadow: false,
        useHTML: true,
        headerFormat: '<span style="font-size: 0.8rem"><b><span style="color: #00948E">{point.key.isPredicted}</span>\
        {point.key.period}</b></span><br><br>',
        style: { fontSize: CHART_FONTS.font4 },
        valueSuffix: ' M',
        pointFormat: '{series.name}:<b> $ {point.y:.2f}M</b><br>',
        pointFormatter(): string {
          const colorDot = '<span style="color: ' + this.color + '">&#9679;</span>';
          if (this.y) {
            const prefix = (this.series.name === 'TVPI' || this.series.name === 'DPI') ? `x${this.y.toFixed(2)}` : `${this.y.toFixed(2)}M`;
            const suffix = '<br>';
            return `${colorDot} ${this.series.name}: <b>${prefix}</b>${suffix}`;
          }
          return `${colorDot} ${this.series.name}: <b>-</b><br>`;
        },
      },
      legend: {
        itemStyle: { fontSize: CHART_FONTS.font4 },
        align: 'left',
        verticalAlign: 'top',
        floating: false,
      },
      plotOptions: {
        column: {
          width: 100,
          stacking: 'normal',
          pointPadding: 0.1,
          groupPadding: 0.1,
          dataLabels: CHART_STYLES.dataLabelsWithDecimals,
        },
      },
      series: [
        {
          name: 'Distribution',
          type: 'column',
          yAxis: 0,
          stack: 'partCap&Dist',
          data: netFpDTO.distribution,
          color: '#FFDD2D',
          dataLabels: {
            enabled: true,
            color: CHART_COLORS.black12,
          },
        },
        {
          name: 'Net asset value',
          type: 'column',
          yAxis: 0,
          stack: 'partCap&Dist',
          data: netFpDTO.nav,
          color: '#B2B2B4',
        },
        {
          name: 'Net gain/(loss)',
          type: 'column',
          yAxis: 0,
          stack: 'Cont & Gain',
          data: netFpDTO.gain,
          color: '#5CC3BB',
        },
        {
          name: 'Contribution',
          type: 'column',
          yAxis: 0,
          stack: 'Cont & Gain',
          data: netFpDTO.contribution,
          color: '#5E6F78',
        },
        {
          name: 'TVPI',
          type: 'spline',
          yAxis: 1,
          data: netFpDTO.tvpi,
          color: '#5E6F78',
          marker: {
            enabled: false,
            lineWidth: 3,
            fillColor: 'white',
          },
        },
        {
          name: 'DPI',
          type: 'spline',
          yAxis: 1,
          data: netFpDTO.dpi,
          visible: false,
          color: CHART_COLORS.orange,
          marker: {
            enabled: false,
            lineWidth: 3,
            fillColor: 'white',
          },
        },
      ],
    };
  }
}
